import { useCallback, useEffect, useMemo, useState } from 'react';
import { backdropClose } from '../utils/backdropClose';
import toast from 'react-hot-toast';
import {
  Plus, Pencil, Trash2, X, Loader2, Wallet, TrendingUp, TrendingDown,
  Tags, Download, IndianRupee, ChevronLeft, ChevronRight, Archive, Filter
} from 'lucide-react';
import { useApi } from '../utils/useApi';
import { useUserContext } from '../contexts/UserContext';

interface ExpenseCategory {
  id: string;
  name: string;
  isSystem: boolean;
  status: string;
}

interface Branch {
  id: string;
  name: string;
  displayName: string;
}

interface Expense {
  id: string;
  amount: string | number;
  expenseDate: string;
  paymentMethod: string;
  vendorName: string | null;
  notes: string | null;
  source: string;
  category: { id: string; name: string };
  branch: Branch | null;
}

interface Summary {
  totalExpenses: number;
  expenseCount: number;
  previousMonthExpenses: number;
  revenue: number;
  profit: number;
  byCategory: { categoryId: string; name: string; amount: number }[];
  byPaymentMethod: { paymentMethod: string; amount: number }[];
}

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'upi', label: 'UPI' },
  { value: 'bank', label: 'Bank' },
  { value: 'card', label: 'Card' },
];

type PayrollFilter = 'all' | 'exclude' | 'only';

const PAYROLL_FILTERS: { value: PayrollFilter; label: string }[] = [
  { value: 'all', label: 'All expenses' },
  { value: 'exclude', label: 'Exclude payroll' },
  { value: 'only', label: 'Payroll only' },
];

const inr = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
}

// ─── Expense add/edit modal ──────────────────────────────────────────────────

function ExpenseModal({
  expense, categories, branches, onClose, onSaved,
}: {
  expense: Expense | null;
  categories: ExpenseCategory[];
  branches: Branch[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { fetchWithAuth } = useApi();
  const isEdit = !!expense;
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    amount: expense ? String(expense.amount) : '',
    expenseDate: expense?.expenseDate?.split('T')[0] || new Date().toISOString().split('T')[0],
    categoryId: expense?.category?.id || '',
    paymentMethod: expense?.paymentMethod || 'cash',
    vendorName: expense?.vendorName || '',
    notes: expense?.notes || '',
    branchId: expense?.branch?.id || '',
  });

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));
  const activeCategories = categories.filter(c => c.status === 'active');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) return toast.error('Enter a valid amount');
    if (!form.categoryId) return toast.error('Pick a category');
    setSaving(true);
    try {
      const body = JSON.stringify({
        amount: Number(form.amount),
        expenseDate: form.expenseDate,
        categoryId: form.categoryId,
        paymentMethod: form.paymentMethod,
        vendorName: form.vendorName || null,
        notes: form.notes || null,
        branchId: form.branchId || null,
      });
      if (isEdit) {
        await fetchWithAuth(`/api/expenses/${expense!.id}`, { method: 'PATCH', body });
      } else {
        await fetchWithAuth('/api/expenses', { method: 'POST', body });
      }
      toast.success(isEdit ? 'Expense updated' : 'Expense added');
      onSaved();
    } catch (err: any) {
      toast.error(err.message || 'Failed to save expense');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" {...backdropClose(onClose)}>
      <div
        className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[92dvh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-base font-bold text-gray-900">{isEdit ? 'Edit Expense' : 'Add Expense'}</h3>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Amount</label>
            <div className="relative">
              <IndianRupee className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="number" min="1" step="0.01" autoFocus={!isEdit}
                value={form.amount} onChange={e => set('amount', e.target.value)}
                placeholder="0"
                className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-lg font-semibold focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Category</label>
            <div className="flex flex-wrap gap-2">
              {activeCategories.map(c => (
                <button
                  key={c.id} type="button"
                  onClick={() => set('categoryId', c.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                    form.categoryId === c.id
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300'
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Date</label>
              <input
                type="date" value={form.expenseDate} onChange={e => set('expenseDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Paid via</label>
              <select
                value={form.paymentMethod} onChange={e => set('paymentMethod', e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
              >
                {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
          </div>

          {branches.length > 1 && (
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Branch (optional)</label>
              <select
                value={form.branchId} onChange={e => set('branchId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
              >
                <option value="">All / not branch-specific</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.displayName || b.name}</option>)}
              </select>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Vendor (optional)</label>
            <input
              type="text" value={form.vendorName} onChange={e => set('vendorName', e.target.value)}
              placeholder="e.g. Aqua Suppliers"
              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Notes (optional)</label>
            <input
              type="text" value={form.notes} onChange={e => set('notes', e.target.value)}
              placeholder="e.g. 5 water cans"
              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
            />
          </div>

          <button
            type="submit" disabled={saving}
            className="w-full py-3 bg-indigo-600 text-white rounded-xl font-semibold text-sm hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {isEdit ? 'Save Changes' : 'Add Expense'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Category management modal ───────────────────────────────────────────────

function CategoryModal({
  categories, onClose, onChanged,
}: {
  categories: ExpenseCategory[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const { fetchWithAuth } = useApi();
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const addCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setBusy('new');
    try {
      await fetchWithAuth('/api/expenses/categories', {
        method: 'POST',
        body: JSON.stringify({ name: newName.trim() }),
      });
      setNewName('');
      onChanged();
    } catch (err: any) {
      toast.error(err.message || 'Failed to add category');
    } finally {
      setBusy(null);
    }
  };

  const setStatus = async (cat: ExpenseCategory, status: 'active' | 'archived') => {
    setBusy(cat.id);
    try {
      await fetchWithAuth(`/api/expenses/categories/${cat.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      onChanged();
    } catch (err: any) {
      toast.error(err.message || 'Failed to update category');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" {...backdropClose(onClose)}>
      <div
        className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[85dvh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-base font-bold text-gray-900">Expense Categories</h3>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <form onSubmit={addCategory} className="flex gap-2">
            <input
              type="text" value={newName} onChange={e => setNewName(e.target.value)}
              placeholder="New category name"
              className="flex-1 px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
            />
            <button
              type="submit" disabled={busy === 'new' || !newName.trim()}
              className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50"
            >
              {busy === 'new' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
            </button>
          </form>

          <div className="divide-y divide-gray-100">
            {categories.map(cat => (
              <div key={cat.id} className="py-2.5 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`text-sm truncate ${cat.status === 'archived' ? 'text-gray-400 line-through' : 'text-gray-800 font-medium'}`}>
                    {cat.name}
                  </span>
                  {cat.isSystem && (
                    <span className="text-[10px] font-bold uppercase tracking-wide bg-gray-100 text-gray-400 px-1.5 py-0.5 rounded">default</span>
                  )}
                </div>
                <button
                  disabled={busy === cat.id}
                  onClick={() => setStatus(cat, cat.status === 'archived' ? 'active' : 'archived')}
                  className="text-xs font-semibold text-gray-400 hover:text-gray-600 flex items-center gap-1 disabled:opacity-50 shrink-0"
                >
                  {busy === cat.id
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <Archive className="w-3.5 h-3.5" />}
                  {cat.status === 'archived' ? 'Restore' : 'Archive'}
                </button>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400">
            Archived categories are hidden from new expenses; existing entries keep them.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function ExpensesPage() {
  const { fetchWithAuth } = useApi();
  const { userContext, loading: userLoading } = useUserContext();

  const [month, setMonth] = useState(currentMonth());
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [payrollFilter, setPayrollFilter] = useState<PayrollFilter>('all');
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [importing, setImporting] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  const ready = !!userContext && !userLoading;

  const loadCategories = useCallback(async () => {
    try {
      const res = await fetchWithAuth('/api/expenses/categories');
      setCategories(res.data || []);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load categories');
    }
  }, [fetchWithAuth]);

  const loadMonth = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, summaryRes] = await Promise.all([
        fetchWithAuth(`/api/expenses?month=${month}`),
        fetchWithAuth(`/api/expenses/summary?month=${month}`),
      ]);
      setExpenses(listRes.data || []);
      setSummary(summaryRes.data || null);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load expenses');
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, month]);

  useEffect(() => {
    if (!ready) return;
    loadCategories();
    fetchWithAuth('/api/branches').then(res => setBranches(res.data || [])).catch(() => {});
  }, [ready, loadCategories, fetchWithAuth]);

  useEffect(() => {
    if (!ready) return;
    loadMonth();
  }, [ready, loadMonth]);

  const handleImportPayslips = async () => {
    setImporting(true);
    try {
      const res = await fetchWithAuth('/api/expenses/import-payslips', {
        method: 'POST',
        body: JSON.stringify({ month }),
      });
      const { imported, skipped } = res.data || {};
      if (imported > 0) {
        toast.success(`Imported ${imported} salary payment${imported === 1 ? '' : 's'}${skipped ? ` (${skipped} already imported)` : ''}`);
        loadMonth();
        loadCategories();
      } else {
        toast(res.message || (skipped ? 'All payslips for this month are already imported.' : 'Nothing to import.'), { icon: 'ℹ️' });
      }
    } catch (err: any) {
      toast.error(err.message || 'Payroll import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleDelete = async (expense: Expense) => {
    if (!window.confirm(`Delete this ${inr.format(Number(expense.amount))} expense?`)) return;
    setDeleting(expense.id);
    try {
      await fetchWithAuth(`/api/expenses/${expense.id}`, { method: 'DELETE' });
      toast.success('Expense deleted');
      loadMonth();
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete expense');
    } finally {
      setDeleting(null);
    }
  };

  const filteredExpenses = useMemo(() => expenses.filter(e => {
    if (categoryFilter && e.category.id !== categoryFilter) return false;
    if (payrollFilter === 'exclude' && e.source === 'payslip') return false;
    if (payrollFilter === 'only' && e.source !== 'payslip') return false;
    return true;
  }), [expenses, categoryFilter, payrollFilter]);

  const isFiltered = !!categoryFilter || payrollFilter !== 'all';
  const filteredTotal = useMemo(
    () => filteredExpenses.reduce((sum, e) => sum + Number(e.amount), 0),
    [filteredExpenses]
  );

  const displayedSpent = isFiltered ? filteredTotal : (summary?.totalExpenses ?? null);
  const displayedProfit = summary && isFiltered ? summary.revenue - filteredTotal : (summary?.profit ?? null);

  const momDelta = summary && summary.previousMonthExpenses > 0 && !isFiltered
    ? ((summary.totalExpenses - summary.previousMonthExpenses) / summary.previousMonthExpenses) * 100
    : null;
  const topCategory = summary?.byCategory?.[0];
  const maxCategoryAmount = topCategory?.amount || 0;

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Wallet className="w-5 h-5 text-red-500" /> Expenses
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Track where your gym's money goes.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowCategoryModal(true)}
            className="px-3 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 flex items-center gap-1.5"
          >
            <Tags className="w-4 h-4" /> Categories
          </button>
          <button
            onClick={handleImportPayslips} disabled={importing}
            className="px-3 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 flex items-center gap-1.5 disabled:opacity-50"
          >
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Import Payroll
          </button>
          <button
            onClick={() => { setEditingExpense(null); setShowExpenseModal(true); }}
            className="px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 flex items-center gap-1.5"
          >
            <Plus className="w-4 h-4" /> Add Expense
          </button>
        </div>
      </div>

      {/* Month picker */}
      <div className="flex items-center flex-wrap gap-2">
        <button
          onClick={() => setMonth(m => shiftMonth(m, -1))}
          className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50"
          aria-label="Previous month"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-sm font-bold text-gray-800 min-w-[140px] text-center">{monthLabel(month)}</span>
        <button
          onClick={() => setMonth(m => shiftMonth(m, 1))}
          disabled={month >= currentMonth()}
          className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30"
          aria-label="Next month"
        >
          <ChevronRight className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-2 sm:ml-auto flex-wrap">
          <Filter className="w-4 h-4 text-gray-400 hidden sm:block" />
          <select
            value={categoryFilter}
            onChange={e => setCategoryFilter(e.target.value)}
            className="px-2.5 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-600 bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
          >
            <option value="">All categories</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select
            value={payrollFilter}
            onChange={e => setPayrollFilter(e.target.value as PayrollFilter)}
            className="px-2.5 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-600 bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
          >
            {PAYROLL_FILTERS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
          {isFiltered && (
            <button
              onClick={() => { setCategoryFilter(''); setPayrollFilter('all'); }}
              className="text-xs font-semibold text-indigo-600 hover:underline shrink-0"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{isFiltered ? 'Spent (filtered)' : 'Total Spent'}</p>
          <p className="text-xl font-bold text-gray-900 mt-1">{displayedSpent !== null ? inr.format(displayedSpent) : '—'}</p>
          {momDelta !== null && (
            <p className={`text-xs font-semibold mt-1 flex items-center gap-1 ${momDelta > 0 ? 'text-red-500' : 'text-green-600'}`}>
              {momDelta > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {Math.abs(momDelta).toFixed(0)}% vs last month
            </p>
          )}
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Collected</p>
          <p className="text-xl font-bold text-gray-900 mt-1">{summary ? inr.format(summary.revenue) : '—'}</p>
          <p className="text-xs text-gray-400 mt-1">Payments received this month</p>
        </div>
        <div className={`rounded-2xl border shadow-sm p-4 ${displayedProfit !== null && displayedProfit < 0 ? 'bg-red-50 border-red-100' : 'bg-green-50 border-green-100'}`}>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{isFiltered ? 'Profit (filtered)' : 'Profit'}</p>
          <p className={`text-xl font-bold mt-1 ${displayedProfit !== null && displayedProfit < 0 ? 'text-red-600' : 'text-green-700'}`}>
            {displayedProfit !== null ? inr.format(displayedProfit) : '—'}
          </p>
          <p className="text-xs text-gray-400 mt-1">Collected − spent</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Top Category</p>
          <p className="text-xl font-bold text-gray-900 mt-1 truncate">{topCategory?.name || '—'}</p>
          {topCategory && <p className="text-xs text-gray-400 mt-1">{inr.format(topCategory.amount)}</p>}
        </div>
      </div>

      {/* Category breakdown */}
      {summary && summary.byCategory.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">By Category</p>
          <div className="space-y-2">
            {summary.byCategory.map(c => (
              <button
                key={c.categoryId}
                onClick={() => setCategoryFilter(f => (f === c.categoryId ? '' : c.categoryId))}
                className={`w-full text-left group ${categoryFilter && categoryFilter !== c.categoryId ? 'opacity-40' : ''}`}
              >
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="font-medium text-gray-700 group-hover:text-indigo-600">{c.name}</span>
                  <span className="font-semibold text-gray-900">{inr.format(c.amount)}</span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-indigo-500 rounded-full"
                    style={{ width: `${maxCategoryAmount ? Math.max(3, (c.amount / maxCategoryAmount) * 100) : 0}%` }}
                  />
                </div>
              </button>
            ))}
          </div>
          {categoryFilter && (
            <button onClick={() => setCategoryFilter('')} className="mt-3 text-xs font-semibold text-indigo-600 hover:underline">
              Clear filter
            </button>
          )}
        </div>
      )}

      {/* Expense list */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-300" /></div>
        ) : filteredExpenses.length === 0 ? (
          <div className="py-16 text-center px-6">
            <Wallet className="w-10 h-10 text-gray-200 mx-auto mb-3" />
            <p className="text-sm font-semibold text-gray-500">
              {expenses.length === 0 ? `No expenses logged for ${monthLabel(month)}` : 'No expenses match this filter'}
            </p>
            {expenses.length === 0 && (
              <p className="text-xs text-gray-400 mt-1">Tap "Add Expense" to log your first one — it takes 15 seconds.</p>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {filteredExpenses.map(exp => (
              <div key={exp.id} className="px-4 sm:px-5 py-3 flex items-center gap-3 hover:bg-gray-50/50">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-gray-800">{exp.category.name}</span>
                    {exp.source === 'payslip' && (
                      <span className="text-[10px] font-bold uppercase tracking-wide bg-blue-50 text-blue-500 px-1.5 py-0.5 rounded">payroll</span>
                    )}
                    {exp.branch && (
                      <span className="text-[10px] font-semibold bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">
                        {exp.branch.displayName || exp.branch.name}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5 truncate">
                    {new Date(exp.expenseDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                    {exp.vendorName ? ` · ${exp.vendorName}` : ''}
                    {exp.notes ? ` · ${exp.notes}` : ''}
                    {` · ${PAYMENT_METHODS.find(m => m.value === exp.paymentMethod)?.label || exp.paymentMethod}`}
                  </p>
                </div>
                <span className="text-sm font-bold text-gray-900 shrink-0">{inr.format(Number(exp.amount))}</span>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => { setEditingExpense(exp); setShowExpenseModal(true); }}
                    className="p-1.5 rounded-lg text-gray-300 hover:text-indigo-600 hover:bg-indigo-50"
                    aria-label="Edit expense"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(exp)}
                    disabled={deleting === exp.id}
                    className="p-1.5 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 disabled:opacity-50"
                    aria-label="Delete expense"
                  >
                    {deleting === exp.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showExpenseModal && (
        <ExpenseModal
          expense={editingExpense}
          categories={categories}
          branches={branches}
          onClose={() => setShowExpenseModal(false)}
          onSaved={() => { setShowExpenseModal(false); loadMonth(); }}
        />
      )}
      {showCategoryModal && (
        <CategoryModal
          categories={categories}
          onClose={() => setShowCategoryModal(false)}
          onChanged={loadCategories}
        />
      )}
    </div>
  );
}
